import React, { useCallback, useMemo, useState } from 'react';
import type {
	WorkflowErrorsMap,
	Transition,
	Status,
	StartNodeLayout,
	GlobalLoopsContainerLayout,
	Coordinates,
} from '@jira/project-settings__project-configuration-commons/src/index.tsx';
import { START_NODE_ID } from '@jira/project-settings__project-configuration-commons/src/model/constants.tsx';
import type { Transitions } from '@jira/project-settings__project-configuration-commons/src/model/types.tsx';
import type { DiagramDirectTransition, DiagramTransition } from '../common/types/diagram.tsx';
import type {
	DiagramNode,
	DrawCompletionCause,
	ItemReference,
	Positionable,
	ViewBoxRect,
} from '../common/types/index.tsx';
import { useLatest } from '../common/utils/use-latest/index.tsx';
import { GLOBAL_LOOPS_CONTAINER_TYPE } from '../common/constants/index.tsx';
import { useControllable } from '../common/utils/use-controllable/index.tsx';
import { DEFAULT_PAN, DEFAULT_ZOOM_LEVEL } from '../common/constants/diagram.tsx';
import Diagram from './diagram/index.tsx';
import ListNavigationWithMove from './list-navigation-with-move/index.tsx';
import {
	getTransitionConnectionInfo,
	toDiagramGlobalLoopsContainerPosition,
	toDiagramStartNodePosition,
	toDiagramStatuses,
	toDiagramTransitions,
} from './utils.tsx';

export type Props = {
	statuses: Status[];
	transitions: Transitions;
	errors?: WorkflowErrorsMap;
	startNodeLayout: StartNodeLayout;
	globalLoopsContainerLayout?: GlobalLoopsContainerLayout;
	zoom?: number;
	isImmutable?: boolean;
	selectedItem?: ItemReference;
	panOffset?: Coordinates;
	collapseTransitionLabels?: boolean;
	shouldShowGlobalLoops?: boolean;
	onSelect?: (item?: ItemReference) => void;
	onNodeUpdate?: (node: DiagramNode) => void;
	onDrawCompleted?: (cause: DrawCompletionCause) => void;
	onViewBoxChanged?: (viewBox: ViewBoxRect) => void;
	onZoomUpdate?: (zoomLevel: number) => void;
	onPanUpdate?: (pan: Coordinates) => void;
	onTransitionUpdate?: (updated: Pick<Transition, 'id' | 'toRef' | 'links'>) => void;
	onTransitionCreate?: (updated: Pick<Transition, 'toRef' | 'links'>) => void;
	onGlobalLoopsContainerMove?: (position: GlobalLoopsContainerLayout) => void;
	onTargetChange?: (targeted: ItemReference | undefined) => void;
};

const AccessibleDiagram = ({
	statuses,
	transitions,
	errors,
	startNodeLayout,
	globalLoopsContainerLayout,
	zoom,
	panOffset,
	isImmutable = false,
	selectedItem,
	onSelect,
	onDrawCompleted,
	onNodeUpdate,
	onViewBoxChanged,
	onZoomUpdate,
	onTransitionUpdate,
	onTransitionCreate,
	onPanUpdate,
	onGlobalLoopsContainerMove,
	onTargetChange,
	collapseTransitionLabels = false,
	shouldShowGlobalLoops = false,
}: Props) => {
	const [targetedItem, setTargetedItem] = useState<ItemReference>();
	const [movingItem, setMovingItem] = useState<Positionable | undefined>(undefined);

	const latestOnSelect = useLatest(onSelect);
	const latestOnDrawCompleted = useLatest(onDrawCompleted);

	const latestOnNodeUpdate = useLatest(onNodeUpdate);
	const latestOnViewBoxChanged = useLatest(onViewBoxChanged);
	const latestOnTransitionUpdate = useLatest(onTransitionUpdate);
	const latestOnTransitionCreate = useLatest(onTransitionCreate);
	const latestOnGlobalLoopsContainerMove = useLatest(onGlobalLoopsContainerMove);
	const latestOnTargetChange = useLatest(onTargetChange);

	const handleTargetChange = useCallback(
		(target?: ItemReference) => {
			setTargetedItem(target);
			latestOnTargetChange.current?.(target);
		},
		[latestOnTargetChange],
	);

	const diagramTransitions = useMemo(
		() => toDiagramTransitions(transitions, errors, statuses),
		[errors, statuses, transitions],
	);

	const initialStatusId = diagramTransitions.find(
		(transition): transition is DiagramDirectTransition =>
			transition.type === 'direct' && transition.source === START_NODE_ID,
	)?.target;

	const diagramStatuses = useMemo(
		() => toDiagramStatuses(statuses, initialStatusId, errors),
		[errors, initialStatusId, statuses],
	);

	const startNodePosition = useMemo(
		() => toDiagramStartNodePosition(startNodeLayout),
		[startNodeLayout],
	);

	const globalLoopsContainerPosition = useMemo(() => {
		return toDiagramGlobalLoopsContainerPosition(globalLoopsContainerLayout);
	}, [globalLoopsContainerLayout]);

	const onRenderComplete = useCallback(() => {
		latestOnDrawCompleted.current?.('INITIAL');
	}, [latestOnDrawCompleted]);

	const onSelectItem = useCallback(
		(item: ItemReference | undefined) => {
			if (!item) {
				latestOnSelect.current?.(undefined);
				return;
			}

			latestOnSelect.current?.({
				id: item.id,
				type: item.type,
			});
		},
		[latestOnSelect],
	);

	const onItemMove = useCallback(
		(payload: Positionable) => {
			if (payload.type === GLOBAL_LOOPS_CONTAINER_TYPE) {
				latestOnGlobalLoopsContainerMove.current?.(payload.position);
			} else {
				latestOnNodeUpdate.current?.({ id: payload.id, layout: payload.position });
			}
		},
		[latestOnGlobalLoopsContainerMove, latestOnNodeUpdate],
	);

	const clearGlobalLoopsContainerPosition = useCallback(() => {
		latestOnGlobalLoopsContainerMove.current?.({});
	}, [latestOnGlobalLoopsContainerMove]);

	const handleTransitionCreateNew = useCallback(
		(transition: DiagramTransition) => {
			latestOnTransitionCreate.current?.(getTransitionConnectionInfo(transition));
		},
		[latestOnTransitionCreate],
	);

	const handleTransitionPortChangeNew = useCallback(
		(transition: DiagramTransition) => {
			latestOnTransitionUpdate.current?.({
				id: transition.id,
				...getTransitionConnectionInfo(transition),
			});
		},
		[latestOnTransitionUpdate],
	);

	const [currentPan, updatePan] = useControllable(panOffset, onPanUpdate, DEFAULT_PAN);

	const [currentZoom, updateZoom] = useControllable(zoom, onZoomUpdate, DEFAULT_ZOOM_LEVEL);

	return (
		<>
			<ListNavigationWithMove
				startNodePosition={startNodePosition}
				globalLoopsContainerPosition={globalLoopsContainerPosition}
				statuses={statuses}
				transitions={transitions}
				errors={errors}
				isImmutable={isImmutable}
				movingItem={movingItem}
				selectedItem={selectedItem}
				zoom={currentZoom}
				pan={currentPan}
				onSelect={latestOnSelect.current}
				onTargetChange={handleTargetChange}
				setMovingItem={setMovingItem}
				onItemMove={onItemMove}
				onZoomChange={updateZoom}
				onPanChange={updatePan}
			/>

			<Diagram
				isImmutable={isImmutable}
				transitions={diagramTransitions}
				startNodePosition={startNodePosition}
				globalLoopsContainerPosition={globalLoopsContainerPosition}
				onClearGlobalLoopsContainerPosition={clearGlobalLoopsContainerPosition}
				statuses={diagramStatuses}
				selectedItem={selectedItem ?? undefined}
				targetedItem={targetedItem}
				movingItem={movingItem}
				zoom={currentZoom}
				pan={currentPan}
				onViewBoxChange={latestOnViewBoxChanged.current}
				onZoomChange={updateZoom}
				onPanChange={updatePan}
				onRenderComplete={onRenderComplete}
				onItemMove={onItemMove}
				onTransitionCreate={handleTransitionCreateNew}
				onTransitionPortChange={handleTransitionPortChangeNew}
				onSelect={onSelectItem}
				collapseTransitionLabels={collapseTransitionLabels}
				shouldShowGlobalLoops={shouldShowGlobalLoops}
			/>
		</>
	);
};

export default AccessibleDiagram;
